/**
 * 69.3b step 3: the bot commander's rules for its side's drops, on the side's knowledge only (its picture, core/picture.ts —
 * never the truth), paced over the match clock as its airstrikes are (`even`: the k-th of N no sooner than (k − 1)/N of the match,
 * the first once the clock has started):
 * - **the dropship** (troops): with at least DROP_RULES.shipMinWaiting of its side waiting to come back, it lands them
 *   DROP_RULES.shipShortPeople short of its focus objective (the point its plan sends most squads to, not its own), on the
 *   line back toward its home — stepped further back while a known enemy is within DROP_RULES.enemyReachPeople of the spot;
 *   their drop squad takes the objective; 69.52 step 4 (DECISIONS 262 (d), the owner: "we'll probably want it eventually so might as
 *   well build it while ships are infinite to understand it"): called **hot** (straight in, pressing on under fire: no bend
 *   round the cover, no break-off) when the side trails on points by HOT_CALL.trailing or more and the cover its side knows
 *   of stands over the landing or across the straight way in from its ship base (core/hotCall.ts) — a drop that would be bent
 *   away or turned back where it is wanted most; 69.37 step 4 (DECISIONS 268 (2) (c), "c": the plan's air element first, the
 *   fetch the fallback; GameConfig.mountedAssault): with the mounted assault on, no fetch while one of its side's plays wants
 *   the ship (core/plays.ts airWanted), and from DROP_RULES.fallbackMinWaiting waiting otherwise;
 * - **the pod drop** (the deep insertion, where the ship would be seen): with at least DROP_RULES.podMinWaiting waiting, onto a
 *   rear objective no one it knows of defends (another side's or nobody's point, not the focus, no known enemy within
 *   DROP_RULES.podRearClearPeople), the farthest from its home; else DROP_RULES.podBehindPeople behind its focus (beyond it
 *   from its home) when no known enemy stands within DROP_RULES.podBehindClearPeople of that spot; its drop squad takes that point;
 * - **base when massing:** while one of its plays waits in its setting phase (the assault waiting for its support, 68.22 /
 *   159 (2)), its waves land at its base (`spawnAt`), forward again once none waits — its own order only (never a human's).
 * Pure; the commander calls them on its re-plan.
 */
import type { GameState, SquadOrder, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT, PHYS_DT } from '../constants';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dacos, dcos, dsin } from '../core/dmath';
import { personSetOf } from '../core/scaleProfile';
import { teamPicture } from '../core/picture';
import { airstrikeHome } from '../core/airstrike';
import { dropshipsLeft, gunshipsLeft, gunshipStation, helisLeft, waitingRiders, shipBase } from '../core/dropship';
import { SHIP_PICKUP, RESCUE } from '../constants/action';
import { rescueLive, rescueRisk, rescueWorth, rescueCost, liftCraft } from '../core/rescue';
import { hotWanted } from '../core/hotCall';
import { airWanted } from '../core/plays';
import { podsLeft } from '../core/pods';
import { GUNSHIP } from '../constants/gunship';
import { dronesFree } from '../core/supplyDrone';
import { supplyLow, nearestCrate } from '../core/supply';
import { SUPPLY } from '../constants';
import { countOf, defaultOn } from '../core/defaultOn';

/** The rules' dials (people, seconds, counts). Starting values. */
export const DROP_RULES = {
  shipMinWaiting: 4, shipShortPeople: 60, shipStepPeople: 30, shipTries: 3, enemyReachPeople: 40, knownSeconds: 10,
  podMinWaiting: 3, podRearClearPeople: 30, podBehindPeople: 40, podBehindClearPeople: 15,
  /** 69.37 step 4: the fetch's threshold with the mounted assault on (the fallback, §5.28's B: from 1 waiting; a proposal) */
  fallbackMinWaiting: 1,
  /** 63.13: a gunship over the focus once the side knows this many enemies within its reach there (GUNSHIP.reachPeople) */
  gunshipMinKnown: 2,
};

export { hotWanted };

type Spot = { planetIndex: number; surfaceDir: Vec3 };

/** `even` pacing over the match clock: the k-th of the side's `issued` no sooner than (k − 1)/N of it (the first once the clock has started; no clock: at once). */
function paced(state: GameState, issued: number, left: number, tick: number): boolean {
  if (left <= 0) return false;
  const limit = state.config.actionTimeLimit, elapsed = limit && limit > 0 ? Math.min(1, (tick * PHYS_DT) / limit) : 1;
  return issued - left < Math.ceil(issued * elapsed);
}

const arcOn = (state: GameState, pi: number, a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * state.planets[pi].radius;
/** The direction `dist` world units from `d` toward `to` (away from it when `dist` is negative) on its planet. */
function toward(state: GameState, pi: number, d: Vec3, to: Vec3, dist: number): Vec3 {
  const n = vecNormalize(d), raw = vecSub(vecNormalize(to), n);
  const t0 = vecSub(raw, vecScale(n, vecDot(raw, n)));
  if (vecLength(t0) < 1e-12) return n;
  const t = vecNormalize(t0), a = dist / state.planets[pi].radius;
  return vecNormalize(vecAdd(vecScale(n, dcos(a)), vecScale(t, dsin(a))));
}

/** The point the commander's plan sends most squads to (by its orders' `pointId`), not its own side's: its focus. Ties: the lower id. */
export function focusPoint(state: GameState, team: number, plan: ReadonlyMap<number, SquadOrder>): { id: number; planetIndex: number; surfaceDir: Vec3 } | null {
  const sites = state.modeState.points?.sites ?? [];
  const n = new Map<number, number>();
  for (const o of plan.values()) {
    const id = (o as { pointId?: number }).pointId;
    if (id !== undefined) n.set(id, (n.get(id) ?? 0) + 1);
  }
  const best = [...n].filter(([id]) => sites.find(s => s.id === id && s.owner !== team)).sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
  const site = best ? sites.find(s => s.id === best[0])! : null;
  return site ? { id: site.id, planetIndex: site.planetIndex, surfaceDir: site.surfaceDir } : null;
}

/** The side's fresh knowledge: the enemies its picture puts on a planet, seen within DROP_RULES.knownSeconds. */
function knownOn(state: GameState, team: number, tick: number, pi: number): Vec3[] {
  const fresh = Math.round(DROP_RULES.knownSeconds / PHYS_DT);
  return teamPicture(state, team, tick).filter(b => b.planetIndex === pi && tick - b.tick <= fresh).map(b => b.surfaceDir);
}

/** The commander's dropship call now, or null: its troops landed short of its focus, out of reach of what it knows, their squad to take it. */
export function dropshipCall(state: GameState, team: number, tick: number, focus: { id: number; planetIndex: number; surfaceDir: Vec3 } | null, heli = false): { at: Spot; order: SquadOrder; hot?: true } | null {
  const a = state.dropships;
  // (69.59 step 1: `heli`, a helicopter's call — paced on the side's helicopters as a dropship's on its dropships)
  const issued = heli ? countOf(state.config, 'helicopters') : a?.issued[team] ?? 0, left = heli ? helisLeft(state, team) : dropshipsLeft(state, team);
  if (!a || !focus || !paced(state, issued, left, tick)) return null;
  // (69.37 step 4: with the mounted assault on, the fetch a fallback — none while a play wants the ship, else from fewer waiting)
  const fallback = !!state.config.mountedAssault;
  if (fallback && airWanted(state, team)) return null;
  if (waitingRiders(state, team).length < (fallback ? DROP_RULES.fallbackMinWaiting : DROP_RULES.shipMinWaiting)) return null;
  const home = airstrikeHome(state, team);
  if (!home) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, known = knownOn(state, team, tick, focus.planetIndex);
  for (let k = 0; k < DROP_RULES.shipTries; k++) {
    const dir = toward(state, focus.planetIndex, focus.surfaceDir, home.surfaceDir, (DROP_RULES.shipShortPeople + k * DROP_RULES.shipStepPeople) * person);
    if (known.some(e => arcOn(state, focus.planetIndex, e, dir) <= DROP_RULES.enemyReachPeople * person)) continue;
    const at = { planetIndex: focus.planetIndex, surfaceDir: dir };
    return { at, order: { kind: 'take', pointId: focus.id }, ...(hotWanted(state, team, at) ? { hot: true as const } : {}) };
  }
  return null;
}

/**
 * 63.13: the commander's gunship call now, or null — over its focus, paced over the match like its other calls, once its fresh
 * picture puts DROP_RULES.gunshipMinKnown enemies within the gunship's reach of it; none while one of its own is up (one at a
 * time) or while a play of its own is live (the play calls it at its assault).
 */
export function gunshipCall(state: GameState, team: number, tick: number, focus: { id: number; planetIndex: number; surfaceDir: Vec3 } | null): { at: Spot } | null {
  const a = state.dropships;
  if (!a || !focus || !countOf(state.config, 'gunships') || !paced(state, countOf(state.config, 'gunships'), gunshipsLeft(state, team), tick)) return null;
  if (a.flights.some(f => f.team === team && f.gunship)) return null;
  if ((state.plays ?? []).some(p => p.team === team && p.phase !== 'done')) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const near = knownOn(state, team, tick, focus.planetIndex).filter(e => arcOn(state, focus.planetIndex, e, focus.surfaceDir) <= GUNSHIP.reachPeople * person);
  if (near.length < DROP_RULES.gunshipMinKnown) return null;
  if ('refused' in gunshipStation(state, team, focus.planetIndex, focus.surfaceDir)) return null;   // (under known cover it cannot clear: not sent to die; it waits)
  return { at: { planetIndex: focus.planetIndex, surfaceDir: focus.surfaceDir } };
}

/** The commander's pod drop now, or null: onto an undefended rear objective, else behind its focus; the drop squad to take that point. */
export function podCall(state: GameState, team: number, tick: number, focus: { id: number; planetIndex: number; surfaceDir: Vec3 } | null): { at: Spot; order: SquadOrder } | null {
  const p = state.pods;
  if (!p || !paced(state, p.issued[team] ?? 0, podsLeft(state, team), tick)) return null;
  if (waitingRiders(state, team).length < DROP_RULES.podMinWaiting) return null;
  const home = airstrikeHome(state, team);
  if (!home) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  // a rear objective no one it knows of defends: the farthest from its home
  const rear = (state.modeState.points?.sites ?? [])
    .filter(s => s.owner !== team && s.id !== focus?.id && s.planetIndex === home.planetIndex)
    .filter(s => !knownOn(state, team, tick, s.planetIndex).some(e => arcOn(state, s.planetIndex, e, s.surfaceDir) <= DROP_RULES.podRearClearPeople * person))
    .sort((a, b) => arcOn(state, b.planetIndex, b.surfaceDir, home.surfaceDir) - arcOn(state, a.planetIndex, a.surfaceDir, home.surfaceDir) || a.id - b.id)[0];
  if (rear) return { at: { planetIndex: rear.planetIndex, surfaceDir: rear.surfaceDir }, order: { kind: 'take', pointId: rear.id } };
  // else behind its focus, beyond it from its home, when that spot is clear of what it knows
  if (!focus) return null;
  const dir = toward(state, focus.planetIndex, focus.surfaceDir, home.surfaceDir, -DROP_RULES.podBehindPeople * person);
  if (knownOn(state, team, tick, focus.planetIndex).some(e => arcOn(state, focus.planetIndex, e, dir) <= DROP_RULES.podBehindClearPeople * person)) return null;
  return { at: { planetIndex: focus.planetIndex, surfaceDir: dir }, order: { kind: 'take', pointId: focus.id } };
}

/**
 * 69.18 (the owner, Oct 21: "for bots with a couple people who are far away from an objective to call in a dropship to pick them
 * up and take them back to base for retasking if available"; GameConfig.shipPickup): the commander's pickup now, or null — of
 * `squads` (its own, none a human orders), one with SHIP_PICKUP.minLiving or more of it alive on foot that stands
 * SHIP_PICKUP.farPeople or more from its base and from every objective on the planet — and SHIP_PICKUP.marginPeople farther
 * from the nearest objective than its base is (the base the nearer start: never a ride away from the fight) — in no live play:
 * the one farthest from the nearest objective (ties: the lower id), fetched from where it stands. Only with its side's wave
 * ship home on its pad. DECISIONS 272 (2) (the owner: "I have no problem with that, but assuming there's a really low shootdown
 * risk for low priority pickups to redeploy"): with no such squad, one whose base is the farther start is fetched to redeploy
 * (`priority: 'redeploy'`, the call refused unless clear of known cover: core/dropship.ts callPickup); none fetched within
 * SHIP_PICKUP.cooldownSeconds of its last; with `since` (the commander's record of when each squad was first seen stranded,
 * strandedSquads), only one stranded SHIP_PICKUP.strandedSeconds.
 */
export function pickupCall(state: GameState, team: number, tick: number, squads: ReadonlyArray<{ id: number; unitIds: number[] }>, since?: ReadonlyMap<number, number>): { squadId: number; at: Spot; priority?: 'redeploy'; craft?: 'heli' } | null {
  const ctx = pickupContext(state, team, tick);
  if (!ctx) return null;
  const inPlay = new Set((state.plays ?? []).filter(p => p.phase !== 'done').flatMap(p => [...p.support, ...p.assault]));
  const cool = Math.round(SHIP_PICKUP.cooldownSeconds / PHYS_DT), picked = state.dropships?.pickedAt ?? {}, stood = Math.round(SHIP_PICKUP.strandedSeconds / PHYS_DT);
  let best: { squadId: number; at: Spot; d: number } | null = null, redeploy: { squadId: number; at: Spot; d: number } | null = null;
  for (const sq of [...squads].sort((a, b) => a.id - b.id)) {
    if (inPlay.has(sq.id) || (picked[sq.id] !== undefined && tick - picked[sq.id] < cool)) continue;
    if (since && !(since.has(sq.id) && tick - since.get(sq.id)! >= stood)) continue;   // (stranded long enough: the commander's record)
    const x = strandedAt(state, ctx, sq);
    if (!x) continue;
    if (x.d < ctx.home + ctx.margin) { if (!redeploy || x.d > redeploy.d) redeploy = { squadId: sq.id, at: x.at, d: x.d }; continue; }   // (272 (2): home the farther start)
    if (best && x.d <= best.d) continue;
    best = { squadId: sq.id, at: x.at, d: x.d };
  }
  const craft = ctx.craft ? { craft: ctx.craft } : {};   // (69.59 step 2: a helicopter's, the wave ship away)
  if (best) return { squadId: best.squadId, at: best.at, ...craft };
  return redeploy ? { squadId: redeploy.squadId, at: redeploy.at, priority: 'redeploy', ...craft } : null;
}

/**
 * 69.62 step 1: a squad of `squads` cut off now — its knot (knotOf) SHIP_PICKUP.farPeople or more from its base, an enemy its
 * side knows of within RESCUE.contactPeople of it, and none of its own side on foot within RESCUE.isolatedPeople (no help near).
 * (§5.31 read cut off as 69.18's stranded — far from every objective — and in contact; measured on Foothold, a stranded squad is
 * almost never in contact: 11 checks of 2,874, against 49 for this.) In id order, with its knot and the enemies near it.
 */
export function cutOffSquads(state: GameState, team: number, tick: number, squads: ReadonlyArray<{ id: number; unitIds: number[] }>): Array<{ id: number; at: Spot; foes: Vec3[] }> {
  const base = shipBase(state, team);
  if (!base) return [];
  const pi = base.planetIndex, person = PERSON_BODY_HEIGHT * personSetOf(state).k, known = knownOn(state, team, tick, pi);
  if (!known.length) return [];
  const out: Array<{ id: number; at: Spot; foes: Vec3[] }> = [];
  for (const sq of [...squads].sort((a, b) => a.id - b.id)) {
    const at = knotOf(state, pi, sq);
    if (!at || arcOn(state, pi, at, base.surfaceDir) < SHIP_PICKUP.farPeople * person) continue;
    const foes = known.filter(e => arcOn(state, pi, e, at) <= RESCUE.contactPeople * person);
    if (!foes.length) continue;
    const help = state.players.some(p => p.alive && p.teamIndex === team && !sq.unitIds.includes(p.id) && !p.boarded && p.planetIndex === pi && arcOn(state, pi, p.surfaceDir, at) <= RESCUE.isolatedPeople * person);
    if (!help) out.push({ id: sq.id, at: { planetIndex: pi, surfaceDir: at }, foes });
  }
  return out;
}

/**
 * 69.62 step 1 (ARCHITECTURE §5.31; GameConfig.hotRescue): the commander's hot rescue now, or null — with a gunship left and none
 * of its own up, a lift (a helicopter left, else its wave ship home; step 3) and no rescue of its own under way. Of `squads` (its own, none a human orders, in no live play,
 * not rescued or fetched within RESCUE.cooldownSeconds), the candidates: a crew who lived through a ship's loss within
 * RESCUE.crewSeconds (its squad, RESCUE.crewLiving of them alive on foot SHIP_PICKUP.farPeople from the base), and a squad cut off
 * (cutOffSquads, since `since` RESCUE.cutOffSeconds: the commander's record). DECISIONS 282 (a), a judgement, not a rule: each
 * weighed, its worth (core/rescue.ts rescueWorth) against the cost (rescueCost), and the one with the most to spare called when
 * its worth is at least its cost (ties: a crew first, then the lower id) — a squad not worth the risk is left. The landing zone
 * is its knot; the gunship circles over the enemies it knows of near it (their centre, RESCUE.offsetPeople at least from the
 * knot; a crew with none known, over the crew). None too risky (core/rescue.ts rescueRisk: under a known site).
 */
export function rescueCall(state: GameState, team: number, tick: number, squads: ReadonlyArray<{ id: number; unitIds: number[] }>, since?: ReadonlyMap<number, number>): { squadId: number; at: Spot; threat: Spot; cause: 'crew' | 'cutOff' } | null {
  if (!state.config.hotRescue || rescueLive(state, team)) return null;
  if (RESCUE.cover && (!countOf(state.config, 'gunships') || gunshipsLeft(state, team) <= 0 || (state.dropships?.flights ?? []).some(f => f.team === team && f.gunship))) return null;
  const ctx = pickupContext(state, team, tick);
  if (!ctx) return null;
  const craft = liftCraft(state, team);   // (step 3: a helicopter the lift while the side has one; core/rescue.ts callRescue picks the same)
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, reach = RESCUE.contactPeople * person;
  const inPlay = new Set((state.plays ?? []).filter(p => p.phase !== 'done').flatMap(p => [...p.support, ...p.assault]));
  const cool = Math.round(RESCUE.cooldownSeconds / PHYS_DT), picked = state.dropships?.pickedAt ?? {}, stood = Math.round(RESCUE.cutOffSeconds / PHYS_DT);
  const lastRescue = new Map<number, number>();
  for (const r of state.rescues ?? []) if (r.team === team) lastRescue.set(r.squadId, Math.max(lastRescue.get(r.squadId) ?? -Infinity, r.calledTick));
  const free = (id: number) => !inPlay.has(id) && !(picked[id] !== undefined && tick - picked[id] < cool) && !(lastRescue.has(id) && tick - lastRescue.get(id)! < cool);
  const known = knownOn(state, team, tick, ctx.pi);
  const near = (at: Vec3) => known.filter(e => arcOn(state, ctx.pi, e, at) <= reach);
  const threatOf = (at: Vec3, foes: Vec3[]): Spot => {
    if (!foes.length) return { planetIndex: ctx.pi, surfaceDir: at };
    const c = vecNormalize(foes.reduce((a, e) => vecAdd(a, vecNormalize(e)), { x: 0, y: 0, z: 0 }));
    const d = arcOn(state, ctx.pi, at, c), off = RESCUE.offsetPeople * person;
    return { planetIndex: ctx.pi, surfaceDir: d >= off ? c : toward(state, ctx.pi, at, c, off) };
  };
  type Cand = { squadId: number; at: Spot; threat: Spot; cause: 'crew' | 'cutOff' };
  let best: { c: Cand; spare: number } | null = null;
  const weigh = (c: Cand, foes: number) => {
    if (rescueRisk(state, team, c.at, c.threat, craft)) return;
    const spare = rescueWorth(state, c.squadId, c.at, c.cause === 'crew') - rescueCost(state, team, c.squadId, c.at, foes, craft);
    if (spare >= 0 && (!best || spare > best.spare)) best = { c, spare };
  };
  // a crashed crew first: its living on foot, their centre, far from the base
  const ofSquad = new Map(squads.map(sq => [sq.id, sq]));
  for (const crew of [...(state.dropships?.crews ?? [])].filter(c => c.team === team && tick - c.tick < Math.round(RESCUE.crewSeconds / PHYS_DT)).sort((a, b) => a.tick - b.tick)) {
    const live = crew.units.map(id => state.players.find(p => p.id === id)).filter((p): p is NonNullable<typeof p> => !!p && p.alive && !p.boarded && !p.mount && p.planetIndex === ctx.pi).sort((a, b) => a.id - b.id);
    if (live.length < RESCUE.crewLiving) continue;
    const sq = [...ofSquad.values()].find(s => s.unitIds.includes(live[0].id));
    if (!sq || !free(sq.id)) continue;
    const sum = live.reduce((a, u) => vecAdd(a, vecNormalize(u.surfaceDir)), { x: 0, y: 0, z: 0 });
    if (vecLength(sum) < 1e-9) continue;
    const at = vecNormalize(sum);
    if (arcOn(state, ctx.pi, at, ctx.base.surfaceDir) < ctx.far) continue;
    const foes = near(at);
    weigh({ squadId: sq.id, at: { planetIndex: ctx.pi, surfaceDir: at }, threat: threatOf(at, foes), cause: 'crew' }, foes.length);
  }
  // and the squads cut off in contact
  for (const x of cutOffSquads(state, team, tick, squads)) {
    if (!free(x.id)) continue;
    if (since && !(since.has(x.id) && tick - since.get(x.id)! >= stood)) continue;
    weigh({ squadId: x.id, at: x.at, threat: threatOf(x.at.surfaceDir, x.foes), cause: 'cutOff' }, x.foes.length);
  }
  return (best as { c: Cand } | null)?.c ?? null;
}

/** 69.18: the squads of `squads` stranded now (SHIP_PICKUP.minLiving alive on foot, `farPeople` from the base and every objective), in id order — the commander keeps since when (pickupCall's `since`). */
export function strandedSquads(state: GameState, team: number, tick: number, squads: ReadonlyArray<{ id: number; unitIds: number[] }>): number[] {
  const ctx = pickupContext(state, team, tick);
  return ctx ? [...squads].sort((a, b) => a.id - b.id).filter(sq => strandedAt(state, ctx, sq)).map(sq => sq.id) : [];
}

/** The centre of squad `sq`'s tightest knot on planet `pi` (its living on foot within SHIP_PICKUP.gatherPeople of the one with the most so near, the lowest id on a tie), or null (fewer than SHIP_PICKUP.minLiving in it). */
function knotOf(state: GameState, pi: number, sq: { unitIds: number[] }): Vec3 | null {
  const all = sq.unitIds.map(id => state.players.find(p => p.id === id)).filter(p => !!p && p.alive && !p.boarded && !p.mount && p.planetIndex === pi).sort((a, b) => a!.id - b!.id);
  const gather = SHIP_PICKUP.gatherPeople * PERSON_BODY_HEIGHT * personSetOf(state).k;
  const knots = all.map(u => all.filter(v => arcOn(state, pi, u!.surfaceDir, v!.surfaceDir) <= gather));
  const live = knots.reduce((best, k) => (k.length > best.length ? k : best), [] as typeof all);
  if (live.length < SHIP_PICKUP.minLiving) return null;
  const sum = live.reduce((a, u) => vecAdd(a, vecNormalize(u!.surfaceDir)), { x: 0, y: 0, z: 0 });
  return vecLength(sum) < 1e-9 ? null : vecNormalize(sum);
}

type PickupContext = { pi: number; base: Spot; sites: Spot[]; far: number; home: number; margin: number; /** 69.59 step 2: the wave ship away or gone, a helicopter's */ craft?: 'heli' };
/** The pickup's ground for `team` now: its wave ship home on its pad, the base, the objectives on the planet; or null. */
function pickupContext(state: GameState, team: number, tick: number): PickupContext | null {
  const pad = state.shipPads?.[team];
  if (!defaultOn(state.config, 'shipPickup')) return null;
  // (69.59 step 2: the wave ship home on its pad first — no ship spent; else one of the side's helicopters)
  const wave = !!pad && !(pad.backAt !== undefined && tick < pad.backAt) && !(state.dropships?.flights ?? []).some(f => f.fromPad && (f.padOf ?? f.team) === team);
  const heli = !wave && countOf(state.config, 'helicopters') && helisLeft(state, team) > 0;
  if (!wave && !heli) return null;
  const base = shipBase(state, team) ?? (pad ? { planetIndex: pad.planetIndex, surfaceDir: pad.dir } : null);
  if (!base) return null;
  const pi = pad && wave ? pad.planetIndex : base.planetIndex;
  const sites = (state.modeState.points?.sites ?? []).filter(s => s.planetIndex === pi);
  if (!sites.length || base.planetIndex !== pi) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  return { pi, base, sites, far: SHIP_PICKUP.farPeople * person, home: Math.min(...sites.map(s => arcOn(state, pi, base.surfaceDir, s.surfaceDir))), margin: SHIP_PICKUP.marginPeople * person, ...(heli ? { craft: 'heli' as const } : {}) };
}
/**
 * Where squad `sq` stands stranded (the centre of its tightest knot: its living within SHIP_PICKUP.gatherPeople of the one with
 * the most so near, the lowest id on a tie — a strewn squad's centre stands near none of them) and how far from the nearest
 * objective; or null (fewer than SHIP_PICKUP.minLiving in the knot).
 */
function strandedAt(state: GameState, c: PickupContext, sq: { unitIds: number[] }): { at: Spot; d: number } | null {
  const at = knotOf(state, c.pi, sq);
  if (!at) return null;
  if (arcOn(state, c.pi, at, c.base.surfaceDir) < c.far) return null;
  const d = Math.min(...c.sites.map(s => arcOn(state, c.pi, at, s.surfaceDir)));
  return d < c.far ? null : { at: { planetIndex: c.pi, surfaceDir: at }, d };
}

/**
 * 69.32 step 2 (the drone_drop sheet's lean: "a squad leader under supplyLow with no crate within the walk calls a drop instead
 * of walking back"; GameConfig.droneDrops): the commander's supply drop now, or null — of `squads` (its own, none a human
 * orders), the first (in id order) with half or more of its living on foot low on supply (core/supply.ts supplyLow), no stocked
 * crate within SUPPLY.walk of it and no drone of its side's already on its way there: small arms where it stands. One a re-plan,
 * only with a drone free.
 */
export function supplyDropCall(state: GameState, team: number, tick: number, squads: ReadonlyArray<{ id: number; unitIds: number[] }>): Spot | null {
  if (!state.config.droneDrops || dronesFree(state, team, tick) <= 0) return null;
  const ps = personSetOf(state), walk = SUPPLY.walk * PERSON_BODY_HEIGHT * ps.k;
  const coming = (state.supplyDrones?.runs ?? []).filter(r => r.team === team && !r.dropped);
  for (const sq of [...squads].sort((a, b) => a.id - b.id)) {
    const live = sq.unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is NonNullable<typeof p> => !!p && p.alive && !p.boarded && !p.mount);
    if (!live.length) continue;
    const low = live.filter(u => supplyLow(u, ps, state.config)).length;
    if (low === 0 || 2 * low < live.length) continue;
    const pi = live[0].planetIndex, sum = live.filter(u => u.planetIndex === pi).reduce((a, u) => vecAdd(a, vecNormalize(u.surfaceDir)), { x: 0, y: 0, z: 0 });
    if (vecLength(sum) < 1e-9) continue;
    const at = vecNormalize(sum);
    if (nearestCrate(state, { teamIndex: team, planetIndex: pi, surfaceDir: at }, walk)) continue;
    if (coming.some(r => r.planetIndex === pi && arcOn(state, pi, r.to, at) <= walk)) continue;
    return { planetIndex: pi, surfaceDir: at };
  }
  return null;
}

/** Is the side massing for a play: one of its plays still in its setting phase (the assault waiting for its support)? */
export function massing(state: GameState, team: number): boolean {
  return (state.plays ?? []).some(p => p.team === team && p.phase === 'setting');
}
